<template>
  <div class="model-preview">
    <div ref="host" class="preview-host" />
    <div v-if="loading" class="preview-message" role="status">正在加载模型…</div>
    <div v-else-if="error" class="preview-message error" role="alert">{{ error }}</div>
    <div v-else-if="!url && !file" class="preview-message">选择一个模型开始预览</div>
    <div class="preview-controls">
      <span>拖动旋转 · 滚轮缩放 · 右键平移</span>
      <button type="button" :disabled="!ready" @click="fit">重置视角</button>
    </div>
  </div>
</template>
<script>
import { markRaw } from 'vue'
import * as THREE from 'three'
import { OrbitControls } from 'three/addons/controls/OrbitControls.js'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'
import { inspectGLB } from '../api/modelCatalog'
import { apiFetch, BASE_URL } from '../api/http'

function release(root) {
  const textures = new Set()
  root.traverse((object) => {
    object.geometry?.dispose()
    const materials = Array.isArray(object.material) ? object.material : [object.material]
    materials.filter(Boolean).forEach((material) => {
      Object.values(material).forEach((value) => { if (value?.isTexture) textures.add(value) })
      material.dispose()
    })
  })
  textures.forEach(texture => texture.dispose())
  root.removeFromParent()
}

export default {
  name: 'ModelPreview',
  props: { url: { type: String, default: '' }, file: { default: null }, interactive: { type: Boolean, default: true } },
  emits: ['ready', 'error'],
  data: () => ({ engine: null, loading: false, error: '', ready: false, sequence: 0 }),
  mounted() {
    try {
      const renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true })
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2))
      renderer.domElement.setAttribute('aria-label', '3D 模型预览')
      const scene = new THREE.Scene()
      scene.background = new THREE.Color('#eef3f8')
      const camera = new THREE.PerspectiveCamera(42, 1, 0.01, 10000)
      camera.position.set(4, 3, 5)
      const controls = new OrbitControls(camera, renderer.domElement)
      controls.enableDamping = true
      controls.enabled = this.interactive
      scene.add(new THREE.HemisphereLight(0xffffff, 0x687c94, 3))
      const light = new THREE.DirectionalLight(0xffffff, 3)
      light.position.set(3, 5, 4)
      scene.add(light)
      const fill = new THREE.DirectionalLight(0xb3d5ff, 2)
      fill.position.set(-4, 2, -3); scene.add(fill)
      this.engine = markRaw({ renderer, scene, camera, controls, model: null })
      this.$refs.host.appendChild(renderer.domElement)
      const resize = () => {
        const width = Math.max(1, this.$refs.host.clientWidth)
        const height = Math.max(1, this.$refs.host.clientHeight)
        renderer.setSize(width, height)
        camera.aspect = width / height; camera.updateProjectionMatrix()
      }
      this.engine.observer = new ResizeObserver(resize)
      this.engine.observer.observe(this.$refs.host); resize()
      if (this.interactive) renderer.setAnimationLoop(() => { controls.update(); renderer.render(scene, camera) })
      this.load()
    } catch (error) { this.error = '无法创建 3D 预览，请启用 WebGL 2 和硬件加速。'; this.$emit('error', this.error) }
  },
  beforeUnmount() {
    this.sequence++
    if (!this.engine) return
    const e = this.engine
    e.abort?.abort(); e.observer?.disconnect(); e.renderer.setAnimationLoop(null); e.controls.dispose()
    release(e.scene); e.renderer.dispose(); e.renderer.forceContextLoss(); e.renderer.domElement.remove()
  },
  watch: { url() { this.load() }, file() { this.load() } },
  methods: {
    async load() {
      if (!this.engine) return
      const run = ++this.sequence
      const e = this.engine
      e.abort?.abort()
      if (e.model) { release(e.model); e.model = null }
      this.ready = false; this.error = ''; this.loading = Boolean(this.file || this.url)
      if (!this.loading) return
      let model
      try {
        let buffer
        if (this.file) { buffer = await this.file.arrayBuffer(); inspectGLB(buffer) }
        else {
          e.abort = new AbortController()
          const privatePath = this.url.startsWith(`${BASE_URL}/api/models/`) ? this.url.slice(BASE_URL.length) : null
          const response = privatePath
            ? await apiFetch(privatePath, { signal: e.abort.signal })
            : await fetch(this.url, { signal: e.abort.signal })
          if (!response.ok) throw new Error(`模型加载失败（${response.status}）`)
          buffer = await response.arrayBuffer()
        }
        if (run !== this.sequence) return
        const gltf = await new GLTFLoader().parseAsync(buffer, '')
        model = gltf.scene
        if (run !== this.sequence) { release(model); return }
        let meshCount = 0
        model.traverse(object => { if (object.isMesh) meshCount++ })
        const box = new THREE.Box3().setFromObject(model)
        const size = box.getSize(new THREE.Vector3())
        const extent = Math.max(size.x, size.y, size.z)
        if (!meshCount || !Number.isFinite(extent) || extent <= 0) throw new Error('模型没有有效的可见网格。')
        const center = box.getCenter(new THREE.Vector3())
        const wrapper = new THREE.Group()
        model.position.sub(center)
        wrapper.add(model); wrapper.scale.setScalar(40 / extent)
        e.model = wrapper; e.scene.add(wrapper)
        this.ready = true; this.fit()
        e.renderer.render(e.scene, e.camera)
        const thumbnail = document.createElement('canvas')
        thumbnail.width = 240; thumbnail.height = Math.round(240 / e.camera.aspect)
        thumbnail.getContext('2d').drawImage(e.renderer.domElement, 0, 0, thumbnail.width, thumbnail.height)
        this.$emit('ready', { thumbnail: thumbnail.toDataURL('image/png'), meshes: meshCount })
      } catch (error) {
        if (model && !e.model) release(model)
        if (run !== this.sequence) return
        this.error = error.message || '模型无法加载，请检查 GLB 文件。'
        this.$emit('error', this.error)
      } finally { if (run === this.sequence) this.loading = false }
    },
    fit() {
      const e = this.engine
      if (!e?.model) return
      const box = new THREE.Box3().setFromObject(e.model)
      const radius = Math.max(1, box.getSize(new THREE.Vector3()).length() / 2)
      const fov = Math.atan(Math.tan(THREE.MathUtils.degToRad(e.camera.fov / 2)) * Math.min(1, e.camera.aspect))
      e.controls.target.copy(box.getCenter(new THREE.Vector3()))
      e.camera.position.copy(e.controls.target).add(new THREE.Vector3(1, 0.7, 1.2).normalize().multiplyScalar(radius / Math.sin(fov) * 1.1))
      e.controls.update()
    }
  }
}
</script>
<style scoped>
.model-preview { height: 100%; min-height: 320px; position: relative; background: #eef3f8; overflow: hidden; border-radius: 12px; }
.preview-host { position: absolute; inset: 0; }
.preview-host :deep(canvas) { display: block; touch-action: none; }
.preview-message { position: absolute; inset: 0; display: grid; place-content: center; padding: 30px; color: #64748b; background: #eef3f8e8; text-align: center; }
.error { color: #b91c1c; }
.preview-controls { position: absolute; bottom: 10px; left: 12px; right: 12px; display: flex; align-items: center; justify-content: space-between; gap: 8px; font-size: 12px; color: #64748b; }
button { border: 1px solid #cbd5e1; border-radius: 6px; background: white; padding: 7px 10px; cursor: pointer; }
</style>

